import React, { useEffect, useRef, useState } from "react";
import { FindAllCandidatesFilter } from "../../../Interface";
import { Input, Button, Select } from "antd";
import {
  ClearOutlined,
  ExportOutlined,
  FilterOutlined,
  SendOutlined,
} from "@ant-design/icons";

const sortOption: {
  value: FindAllCandidatesFilter["sort"];
  label: string;
}[] = [
  {
    label: "Recently Applied",
    value: "recently.applied",
  },
  {
    label: "Oldest Applied",
    value: "oldest.applied",
  },
  {
    label: "Recently Uploaded",
    value: "recently.uploaded",
  },
  {
    label: "Name A-Z",
    value: "name.a.z",
  },
  {
    label: "Name Z-A",
    value: "name.z.a",
  },
  {
    label: "Experience (High to Low)",
    value: "experience.years.desc",
  },
  {
    label: "Experience (Low to High)",
    value: "experience.years.asc",
  },
];

interface TalentPoolFilterMenuProps {
  loading: boolean;
  filters: FindAllCandidatesFilter;
  clearFilter: () => void;
  updateFilters: (key: keyof FindAllCandidatesFilter, value: any) => void;
}

const TalentPoolFilterMenu: React.FC<TalentPoolFilterMenuProps> = ({
  loading,
  filters,
  clearFilter,
  updateFilters,
}) => {
  const [querySearchText, setQuerySearchText] = useState<string | undefined>(
    undefined,
  );
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const hasAnyFilters = !!(
    filters.sort ||
    filters.normalSearch ||
    filters.querySearch
  );

  const handleSearch = (value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    updateFilters("normalSearch", value);
  };

  const handleSortFilter = (value: string | undefined) => {
    updateFilters("sort", value || null);
  };

  const handleQuerySearch = () => {
    if (!querySearchText?.trim()) {
      setQuerySearchText(undefined);
      return;
    }
    updateFilters("querySearch", querySearchText);
  };

  const handleClear = () => {
    setQuerySearchText(undefined);
    setSearchText(undefined);
    clearFilter();
  };

  useEffect(() => {
    setSearchText(filters.normalSearch || undefined);
    setQuerySearchText(filters.querySearch || undefined);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchText !== (filters.normalSearch || "")) {
      debounceRef.current = setTimeout(() => {
        updateFilters("normalSearch", searchText || null);
      }, 500);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, filters.normalSearch]);

  return (
    <div className="talent-pool-filter-menu">
      <div className="ai-prompt-input">
        <img
          src="/icons/ai-stars.svg"
          alt="AI"
          className="ai-prompt-input__icon"
        />
        <Input
          variant="borderless"
          placeholder="Search talent pools with AI"
          className="ai-prompt-input__textarea"
          disabled={loading}
          size="middle"
          value={querySearchText}
          onChange={(e) => {
            setQuerySearchText(e.target.value);
            if (!e.target.value) {
              updateFilters("querySearch", null);
            }
          }}
          onFocus={() => {
            if (searchText || filters.normalSearch) {
              setSearchText(undefined);
              updateFilters("normalSearch", null);
            }
          }}
          onPressEnter={handleQuerySearch}
          allowClear
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          className="ai-prompt-input__send"
          loading={loading}
          size="middle"
          onClick={handleQuerySearch}
        />
      </div>
      <div className="talent-pool-filter-menu-items-wrapper">
        <div className="talent-pool-filter-menu-items">
          <Input.Search
            enterButton
            size="middle"
            disabled={loading}
            placeholder="Search by name, email or phone"
            allowClear
            onSearch={handleSearch}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onFocus={() => {
              if (querySearchText || filters.querySearch) {
                setQuerySearchText(undefined);
                updateFilters("querySearch", null);
              }
            }}
          />
          <Select
            allowClear
            size="middle"
            options={sortOption}
            disabled={loading}
            value={filters.sort}
            onChange={handleSortFilter}
            placeholder={
              <span>
                <FilterOutlined /> Sort by
              </span>
            }
          />
          <Button
            onClick={handleClear}
            size="middle"
            icon={<ClearOutlined />}
            disabled={loading || !hasAnyFilters}
          />
        </div>
        <Button
          type="primary"
          size="middle"
          iconPosition="end"
          icon={<ExportOutlined />}
        >
          Export
        </Button>
      </div>
    </div>
  );
};

export default TalentPoolFilterMenu;
